import { createRoute } from '@tanstack/react-router';
import { getAuthUserPhoneQueryOptions } from '@Pages/inviteTeamMember/api/authUserPhone.api';
import { getAuthUserAddressQueryOptions } from '@Pages/inviteTeamMember/api/authUserAddress.api';
import { getAuthUserIdentityQueryOptions } from '@Pages/inviteTeamMember/api/authUserIdentity.api';
import { fetchCountries } from '@Hooks/api/fetchCountries.api';
import { GeneralSpinner } from '@Components/generalSpinner';
import { beforeLoadGeneral } from '@Router/middleware';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { getValidateAccountantInviteQueryOptions } from '@Pages/accountantPortalOnboarding/api/accountantPortal.api';
import { populateAccountantPortalData } from '@Services/init/flow-identifier-service/accountantPortalOnboarding.service';
import { useBoundStore } from '@Store/useBoundStore';
import InviteTeamMemberFinished from '@Pages/inviteTeamMember/InviteTeamMemberFinished/InviteTeamMemberFinished';
import InviteTeamMemberIntro from '@Pages/inviteTeamMember/InviteTeamMemberIntro/InviteTeamMemberIntro';
import { Phone } from '@Pages/happyFlowPages/phone/Phone';
import { HomeAddress } from '@Pages/happyFlowPages/homeAddress/HomeAddress';
import { Identity } from '@Pages/happyFlowPages/identity/Identity';
import { VerifyPhone } from '@Pages/inviteTeamMember/VerifyPhone/VerifyPhone';
import { SetPassword } from '@Pages/inviteTeamMember/SetPassword/SetPassword';
import { AuthUserDocVPage } from '@Pages/inviteTeamMember/AuthUserDocV/AuthUserDocVPage';
import { AccountantPortalWelcome } from '@Pages/accountantPortalOnboarding/AccountantPortalWelcome/AccountantPortalWelcome';
import { AccountantPortalComplete } from '@Pages/accountantPortalOnboarding/AccountantPortalComplete/AccountantPortalComplete';
import { rootRoute } from './rootRoute';
import type { RegionCode } from 'google-libphonenumber';

const inviteTeamMemberIntroRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.INVITE_TEAM_MEMBER_INTRO].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.INVITE_TEAM_MEMBER_INTRO,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });
  },
  component: () => <InviteTeamMemberIntro />,
});

const verifyPhoneNumberRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.VERIFY_PHONE_NUMBER].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.VERIFY_PHONE_NUMBER,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });
  },
  component: () => <VerifyPhone />,
});

const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.SET_PASSWORD].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.SET_PASSWORD,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });
  },
  component: () => <SetPassword />,
});

const inviteTeamMemberFinishedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.INVITE_TEAM_MEMBER_FINISHED].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.INVITE_TEAM_MEMBER_FINISHED,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });
  },
  component: () => <InviteTeamMemberFinished />,
});

const authUserPhoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.AUTH_USER_PHONE].path,
  beforeLoad: async ({ context }) => {
    beforeLoadGeneral({
      page: RoutesMap.AUTH_USER_PHONE,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });

    const { customerId, authorizedUserId } = useBoundStore.getState().onboardingSlice;
    if (!customerId) return;
    const queryOptions = getAuthUserPhoneQueryOptions(customerId, authorizedUserId);

    try {
      const data = await context.queryClient.ensureQueryData(queryOptions);
      if (data?.phoneNumber) {
        const { getCustomerData, setCustomerData } = useBoundStore.getState();
        const customerToUpdate = { ...getCustomerData() };
        customerToUpdate.customerData.phoneNumber = data.phoneNumber;
        customerToUpdate.customerData.phoneCountryCode =
          (data.phoneNumberCountryCode as RegionCode) || 'US';
        setCustomerData(customerToUpdate);
      }
    } catch {
      // Prefill is best-effort
    }
  },
  component: () => <Phone originRoute={RoutesMap.AUTH_USER_PHONE} />,
  wrapInSuspense: true,
  pendingComponent: () => <GeneralSpinner />,
});

const authUserIdentityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.AUTH_USER_IDENTITY].path,
  beforeLoad: async ({ context }) => {
    beforeLoadGeneral({
      page: RoutesMap.AUTH_USER_IDENTITY,
      shouldUpdateServerNavStack: true,
      shouldAvoidAuthCheck: true,
    });
    await context.queryClient.prefetchQuery({
      queryKey: ['countries', false, false],
      queryFn: () => fetchCountries(false, false),
      staleTime: Infinity,
    });

    const { customerId, authorizedUserId } = useBoundStore.getState().onboardingSlice;
    const queryOptions = getAuthUserIdentityQueryOptions(customerId, authorizedUserId);

    try {
      const data = await context.queryClient.ensureQueryData(queryOptions);
      if (data) {
        const { getCustomerData, setCustomerData } = useBoundStore.getState();
        const customerToUpdate = { ...getCustomerData() };
        if (data.ssn) customerToUpdate.customerData.ssn = data.ssn;
        if (data.birthDate) customerToUpdate.customerData.birthDate = data.birthDate;
        if (data.idCountry) customerToUpdate.customerData.idCountry = data.idCountry;
        if (data.idNumber) customerToUpdate.customerData.idNumber = data.idNumber;
        if (data.idGovNumber) customerToUpdate.customerData.idGovNumber = data.idGovNumber;
        if (data.idExpirationDate)
          customerToUpdate.customerData.idExpirationDate = data.idExpirationDate;
        if (data.idType) customerToUpdate.customerData.idType = data.idType;
        setCustomerData(customerToUpdate);
      }
    } catch {
      // Prefill is best-effort
    }
  },
  loader: () => fetchCountries(false, false),
  component: () => <Identity originRoute={RoutesMap.AUTH_USER_IDENTITY} />,
  wrapInSuspense: true,
  pendingComponent: () => <GeneralSpinner />,
});

const authUserHomeAddressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.AUTH_USER_HOME_ADDRESS].path,
  beforeLoad: async ({ context }) => {
    beforeLoadGeneral({
      page: RoutesMap.AUTH_USER_HOME_ADDRESS,
      shouldUpdateServerNavStack: true,
      shouldAvoidAuthCheck: true,
    });

    const { customerId, authorizedUserId } = useBoundStore.getState().onboardingSlice;
    const queryOptions = getAuthUserAddressQueryOptions(customerId, authorizedUserId);

    try {
      const data = await context.queryClient.ensureQueryData(queryOptions);
      if (data?.line1) {
        const { getCustomerData, setCustomerData } = useBoundStore.getState();
        const customerToUpdate = { ...getCustomerData() };
        customerToUpdate.customerData.customerAddressData = {
          ...customerToUpdate.customerData.customerAddressData,
          line1: data.line1,
          line2: data.line2 || '',
          city: data.city,
          state: data.state || '',
          postalCode: data.postalCode,
          country: data.country || '',
        };
        setCustomerData(customerToUpdate);
        if (data.country && data.country !== 'US') {
          useBoundStore.getState().setAuthUserIsUsFlow(false);
        }
      }
    } catch {
      // Prefill is best-effort
    }
  },
  component: () => <HomeAddress originRoute={RoutesMap.AUTH_USER_HOME_ADDRESS} />,
  wrapInSuspense: true,
  pendingComponent: () => <GeneralSpinner />,
});

const authUserDocVRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.AUTH_USER_DOCV].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.AUTH_USER_DOCV,
      shouldUpdateServerNavStack: true,
      shouldAvoidAuthCheck: true,
      stepToReturn: RoutesMap.AUTH_USER_IDENTITY,
    });
  },
  component: () => <AuthUserDocVPage />,
});

const accountantPortalWelcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.ACCOUNTANT_PORTAL_WELCOME].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.ACCOUNTANT_PORTAL_WELCOME,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });

    const authorizedUserId = useBoundStore.getState().authOnboardingSlice.authorizedUserId || '';

    return {
      validateInviteQuery: getValidateAccountantInviteQueryOptions(authorizedUserId),
    };
  },
  loader: async ({ context }) => {
    const data = await context.queryClient.ensureQueryData(context.validateInviteQuery);
    const authorizedUserId = useBoundStore.getState().authOnboardingSlice.authorizedUserId!;
    populateAccountantPortalData(authorizedUserId, data);
  },
  component: () => <AccountantPortalWelcome />,
  wrapInSuspense: true,
  pendingComponent: () => <GeneralSpinner />,
});

const accountantPortalCompleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.ACCOUNTANT_PORTAL_COMPLETE].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.ACCOUNTANT_PORTAL_COMPLETE,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });
  },
  component: () => <AccountantPortalComplete />,
});

export const inviteTeamMemberRoutes = [
  inviteTeamMemberIntroRoute,
  verifyPhoneNumberRoute,
  setPasswordRoute,
  inviteTeamMemberFinishedRoute,
  authUserPhoneRoute,
  authUserIdentityRoute,
  authUserHomeAddressRoute,
  authUserDocVRoute,
  accountantPortalWelcomeRoute,
  accountantPortalCompleteRoute,
];
